import { FormEvent, useRef, useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import type { Crumb } from "../utils/structuredData";
import SlotPicker from "../components/SlotPicker";
import BookingConfirmation from "../components/BookingConfirmation";
import ExistingMeeting from "../components/ExistingMeeting";
import GuestFields from "../components/GuestFields";
import { cleanGuests } from "../utils/guests";
import { useLandingContext } from "../contexts/LandingContext";
import {
  type ContactField,
  CONTACT_FIELDS,
  emptyContact,
  primaryButton,
  secondaryButton,
  SERVER_FIELDS,
  validateContact,
} from "../utils/bookingContact";
import {
  type Availability,
  type Booking,
  type ExistingBooking,
  type Guest,
  browserTimezone,
  newIdempotencyKey,
  SchedulerError,
  schedulerApi,
} from "../services/schedulerApi";

const crumbs: Crumb[] = [
  { name: "Home", path: "/" },
  { name: "Book a Free Automation Assessment", path: "/book" },
];

type Field = ContactField;
const FIELDS = CONTACT_FIELDS;
const emptyForm = emptyContact;
const secondary = secondaryButton;
const primary = primaryButton;

// The "Book a Free Automation Assessment" page: details form -> slot picker -> confirmation. A visitor who already
// has an upcoming meeting is shown that meeting instead of new times.
const BookAssessment: React.FC = () => {
  const { utmId, utmSource, referredBy } = useLandingContext();
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [intakeId, setIntakeId] = useState<string | null>(null);
  const [availability, setAvailability] = useState<Availability | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [booking, setBooking] = useState<Booking | null>(null);
  const [existing, setExisting] = useState<ExistingBooking | null>(null);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [guestsInvalid, setGuestsInvalid] = useState(false);
  // Reused while the same slot is retried, so a double-click or flaky network books once.
  const bookingKey = useRef<{ slot: string; key: string } | null>(null);

  const validate = () => validateContact(form);

  const startOver = () => {
    setAvailability(null); setIntakeId(null); setExisting(null); setNotice(null); setSelected(null);
  };

  const onSubmitDetails = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const found = validate();
    setErrors(found);
    setNotice(null);
    if (Object.keys(found).length > 0) return;

    setBusy(true);
    try {
      const result = await schedulerApi.createIntake({
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        job_title: form.jobTitle.trim(),
        company: form.company.trim(),
        email: form.email.trim().toLowerCase(),
        timezone: browserTimezone(),
        utm_source: utmSource,
        utm_id: utmId,
        ...(referredBy ? { referred_by: referredBy } : {}),
      });
      if ("existing_booking" in result) {
        setExisting(result.existing_booking);
        return;
      }
      setIntakeId(result.intake_id);
      setAvailability(await schedulerApi.availability({ intake: result.intake_id }));
      setSelected(null);
    } catch (error) {
      if (error instanceof SchedulerError && error.fields.length > 0) {
        setErrors(Object.fromEntries(error.fields.filter((f) => SERVER_FIELDS[f])
          .map((f) => [SERVER_FIELDS[f], "Please check this field."])));
      }
      setNotice(error instanceof SchedulerError ? error.message : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const onConfirm = async () => {
    if (!intakeId || !selected) return;
    const invited = cleanGuests(guests);
    setGuestsInvalid(invited === null);
    if (invited === null) return;
    const attempt = `${selected}|${JSON.stringify(invited)}`;
    if (bookingKey.current?.slot !== attempt) bookingKey.current = { slot: attempt, key: newIdempotencyKey() };
    setBusy(true);
    setNotice(null);
    try {
      const result = await schedulerApi.book(
        { intake_id: intakeId, start: selected, ...(invited.length ? { guests: invited } : {}) },
        bookingKey.current.key);
      setBooking(result.booking);
    } catch (error) {
      if (error instanceof SchedulerError && error.code === "ALREADY_BOOKED" && error.existing) {
        setExisting(error.existing);  // Booked elsewhere (another tab or a colleague) while choosing a time.
        return;
      }
      if (error instanceof SchedulerError && error.code === "SLOT_TAKEN" && error.slots && availability) {
        setAvailability({ ...availability, slots: error.slots });
        setSelected(null);
      } else if (error instanceof SchedulerError && ["INTAKE_EXPIRED", "INTAKE_NOT_FOUND"].includes(error.code)) {
        setIntakeId(null);
        setAvailability(null);
      }
      setNotice(error instanceof SchedulerError ? error.message : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const step = booking ? "done" : existing ? "existing" : availability ? "slots" : "details";

  return (
    <div className="relative pt-6 pb-16">
      <Seo
        title="Book a Free Automation Assessment | Contextral"
        description="Pick a time for a free automation assessment with a Contextral engagement lead."
      />
      <section>
        <Breadcrumbs items={crumbs} />
        <div className="text-center mb-10 sm:mb-14">
          <h1 className="font-bold text-text-primary mb-4 text-3xl sm:text-4xl lg:text-5xl leading-tight">
            Book a Free Automation Assessment
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-text-secondary max-w-4xl mx-auto leading-relaxed">
            {step === "details" && "Tell us who you are, then pick a time that suits you."}
            {step === "slots" && availability &&
              `Choose a ${availability.meeting_minutes}-minute slot with ${availability.lead.name}.`}
            {step === "done" && "We look forward to meeting you."}
            {step === "existing" && "Your meeting is already on the calendar."}
          </p>
        </div>
      </section>
      <section className="max-w-3xl mx-auto">
        <div className="rounded-2xl border border-borderSubtle bg-surface shadow-xl p-6 sm:p-8 md:p-10">
          {notice && <p role="alert" className="mb-6 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{notice}</p>}

          {step === "details" && (
            <form noValidate onSubmit={onSubmitDetails} className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {FIELDS.map((field) => (
                <div key={field.name} className={field.name === "email" ? "md:col-span-2" : undefined}>
                  <label htmlFor={field.name} className="block text-sm font-medium text-text-primary mb-2">
                    {field.label} *
                  </label>
                  <input
                    id={field.name}
                    name={field.name}
                    type="text"
                    inputMode={field.inputMode}
                    autoComplete={field.autoComplete}
                    value={form[field.name]}
                    aria-invalid={Boolean(errors[field.name])}
                    aria-describedby={errors[field.name] ? `${field.name}-error` : undefined}
                    onChange={(event) => setForm((f) => ({ ...f, [field.name]: event.target.value }))}
                    className={`w-full rounded-lg border bg-white px-4 py-3 text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                      errors[field.name] ? "border-red-500" : "border-borderSubtle"
                    }`}
                  />
                  {errors[field.name] && (
                    <p id={`${field.name}-error`} className="text-red-500 text-xs mt-1">{errors[field.name]}</p>
                  )}
                </div>
              ))}
              <button type="submit" disabled={busy} className={`${primary} group md:col-span-2 w-full`}>
                {busy ? "Finding times..." : "See available times"}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </form>
          )}

          {step === "slots" && availability && (
            <div>
              <SlotPicker
                slots={availability.slots}
                timeZone={availability.timezone}
                selected={selected}
                onSelect={setSelected}
                disabled={busy}
              />
              <GuestFields guests={guests} onChange={setGuests} disabled={busy} invalid={guestsInvalid} />
              <div className="mt-8 flex flex-col-reverse sm:flex-row gap-3 sm:justify-between">
                <button type="button" onClick={startOver} disabled={busy} className={secondary}>
                  <ArrowLeft className="mr-2 w-5 h-5" /> Edit details
                </button>
                <button type="button" onClick={() => void onConfirm()} disabled={busy || !selected} className={primary}>
                  {busy ? "Booking..." : "Confirm booking"}
                </button>
              </div>
            </div>
          )}

          {step === "existing" && existing && (
            <>
              <ExistingMeeting existing={existing} email={form.email.trim().toLowerCase()} company={form.company.trim()} />
              <div className="flex justify-center">
                <button type="button" onClick={startOver} className={secondary}>
                  <ArrowLeft className="mr-2 w-5 h-5" /> Use different details
                </button>
              </div>
            </>
          )}

          {step === "done" && booking && <BookingConfirmation booking={booking} />}
        </div>
      </section>
    </div>
  );
};

export default BookAssessment;
